import { useAuthQuery } from './useAuthQuery';
import { useAuth } from './useAuth';
import type { UserRoleData } from '@/types/auth';

// Simplified hook using standardized auth query pattern
export const useUserRoles = () => {
  const { user } = useAuth();
  
  const query = useAuthQuery<UserRoleData[]>({
    queryKey: ['user-roles', user?.id],
    queryFn: async () => {
      const { fetchUserRoles } = await import('@/lib/queries/authQueries');
      return fetchUserRoles(user?.id || '');
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return query;
};

// Removed useUserPermissions to eliminate race condition

export const useUserPermissionCodes = () => {
  const { user } = useAuth();
  return useAuthQuery<string[]>({
    queryKey: ['user-permission-codes', user?.id],
    queryFn: async () => {
      const { supabase } = await import('@/integrations/supabase/client');
      const { data, error } = await supabase
        .rpc('get_user_permission_codes', { p_user_id: user?.id || '' });
      if (error) throw error;
      return (data as string[] | null) ?? [];
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!user?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useCheckPermission = (permissionCode: string) => {
  const { user } = useAuth();
  
  // Feature flag for strict permission checks - reduces staleTime to 0 when enabled
  const ENABLE_STRICT_PERMISSION_CHECK = import.meta.env.VITE_STRICT_PERMISSION_CHECK === 'true';
  
  const codesQuery = useUserPermissionCodes();
  return {
    ...codesQuery,
    data: codesQuery.data ? codesQuery.data.includes(permissionCode) : undefined,
  } as typeof codesQuery & { data: boolean | undefined };
};